import React, { useContext, useState } from "react";
import styled, { ThemeContext } from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import { ChevronRight } from "react-feather";
import { get, isEmpty } from "lodash";

import { ItemType } from "../../store/models/itemType";

import { SET_ACTIVE_MENU_PATH } from "../../store/actions/configurationActions";
import { getMenuItemPixelValue, getMenuPosition } from "../../utilities/maskUtils";
import { SET_SIDE } from "../../store/actions/sideMenuActions";
import {
    getActiveMenuPath,
    getMaskData,
    getMaskElement,
    getMaskMetadata,
    getAppConfig,
    getLanguage,
    getMetadata,
    getConfigurationString,
    getBackendFlavor,
    getSideMenuSide,
} from "../../store/sagas/selectors";
import { ChildItem, Item } from "../../models/item";
import GaReporter from "../../utilities/gaReporter";
import { SdsBackendFlavor } from "../../api/generated";

const MenusContainer = styled.div`
    display: flex;
    flex-flow: column nowrap;
    margin-top: 30px;
`;

const ChevronContainer = styled.div`
    flex: 1 0 auto;
    padding: 29px 26px;
    justify-content: flex-end;
    text-align: end;
    display: flex;
`;

const MenuElement = styled.div`
    cursor: pointer;
    width: 100%;
    height: ${props => props.theme.sideMenu.menu.height};
    background: ${props => props.theme.sideMenu.menu.backgroundColor};
    border-radius: ${props => props.theme.sideMenu.menu.borderRadius};
    box-shadow: ${props => props.theme.sideMenu.menu.shadow};
    display: flex;
    flex-flow: row nowrap;
    justify-content: end;
    align-items: center;
    box-shadow: none;
    transition: box-shadow 0.2s;
    @media (hover) {
        &:hover {
            box-shadow: 0 0 1px 1px #bfbfbf;
        }
    }

    ${({ theme }) => theme.bp.tb`
        width: 340px;
    `}
    ${({ theme }) => theme.bp.xxxl`
        width: 550px;
    `}
`;

const MenuIcon = styled.img`
    height: calc(100% - 8px);
    padding: 5px 4px;
    width: 70px;
    object-fit: cover;
`;

const DummyIcon = styled.div`
    display: inline-block;
    height: calc(100% - 8px);
    padding: 5px 4px;
    width: 70px;
    object-fit: cover;
`;

const ElementText = styled.div`
    align-self: normal;
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const TextContent = styled.p`
    font-size: 13px;
    white-space: pre-wrap;
    word-break: break-word;
    font-family: ${props => props.theme.fontFamily};
    color: ${props => props.theme.textColor};
    padding: 0 5px;
    margin: 0;
    min-height: 47px;
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const MenuContainer = styled.div`
    flex: 1 1 auto;
    margin-bottom: 25px;
`;

const MenuTitle = styled.div`
    font-size: ${props => props.theme.paragraph.fontSize};
    color: ${props => props.theme.textColorSecondary};
    font-family: ${props => props.theme.fontFamily};
    padding: 5px 5px;
    ${({ theme }) => theme.bp.tb`
        width: 320px;
    `}
    ${({ theme }) =>
        theme.bp.xxxl`
                    font-size: 1em;
                `}
`;

const NoMenuItems = styled.div`
    height: 90%;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    position: absolute;
    text-align: center;
    padding-right: 40px;
`;

export const MenusList = ({ items, onItemSelected }: { items: Item[]; onItemSelected: Function }) => {
    const dispatch = useDispatch();

    const theme = useContext(ThemeContext);
    const menuPath = useSelector(getActiveMenuPath);
    const maskMetadata = useSelector(getMaskMetadata);
    const maskData = useSelector(getMaskData);
    const maskElement = useSelector(getMaskElement);
    const menuSide = useSelector(getSideMenuSide);
    const [missingIcons, setMissingIcons] = useState(() => new Set<string>());
    const appConfig = useSelector(getAppConfig);
    const language = useSelector(getLanguage);
    const metadata = useSelector(getMetadata);
    const configString = useSelector(getConfigurationString);
    const backendFlavor = useSelector(getBackendFlavor);

    const onMenuClick = (item: Item) => {
        if (item.type === ItemType.Menu) {
            const pixel = getMenuItemPixelValue(item.id, maskMetadata);
            const sideMenuPosition = getMenuPosition(
                maskData,
                pixel,
                maskElement?.height,
                maskElement?.width,
                menuSide
            );
            dispatch({ type: SET_SIDE, side: sideMenuPosition });
        }

        const text = (item.type === ItemType.Menu ? item.name : item.shortText) || "";
        GaReporter.event("menu_click", "Configurator", text);
        const newMenuPath = [...menuPath, { id: item.id, text }];
        dispatch({ type: SET_ACTIVE_MENU_PATH, activeMenuPath: newMenuPath });
        onItemSelected();
    };

    const getActiveChild = (item: Item): ChildItem | undefined => {
        return item.activeMenuItems && item.activeMenuItems[0];
    };

    const handleIconError = (icon: string) => {
        const newSet = new Set<string>(missingIcons);
        newSet.add(icon);
        setMissingIcons(newSet);
    };

    const renderIcon = (item: Item) => {
        let icon: string | undefined;
        // Use the submenu icon if it has one set.
        if (item.type === ItemType.Submenu && item.thumbnail && !missingIcons.has(item.thumbnail)) {
            icon = item.thumbnail;
        } else {
            // Otherwise use the icon of the first active child item.
            const activeChild = getActiveChild(item);
            icon = activeChild?.thumbnail || item.thumbnail || undefined;
        }
        return icon && !missingIcons.has(icon) ? (
            <MenuIcon alt="Menu" src={icon} onError={() => handleIconError(icon!)} />
        ) : (
            <DummyIcon />
        );
    };

    const getElementText = (item: Item): string => {
        // Use the text of the first active child item for the main menu.
        const activeMenuItem = item.type === ItemType.Menu && getActiveChild(item);
        if (activeMenuItem) {
            return activeMenuItem.displayName || activeMenuItem.shortText?.replace(/\\n/g, "\n") || activeMenuItem.id;
        }
        return item.shortText;
    };

    const useSubMenuHighlight = items?.some(item => !getActiveChild(item));

    if (items.length === 0) {
        return (
            <NoMenuItems>
                <h3>{get(appConfig, `translations.${language}.noItemSelected`)} </h3>
            </NoMenuItems>
        );
    }

    // Logic for hiding submenus for inactive included types in SDS Classic,
    // for example end trims for show walls.
    let configItems: Set<string> | null = null;
    const isHidden = (id: string) => {
        if (backendFlavor !== SdsBackendFlavor.SdsClassic) return false;
        const offItem = metadata?.[id]?.hide_when_active;
        if (!offItem) return false;

        // Lazily extract items from SDS Classic config string.
        if (!configItems) {
            configItems = new Set();
            if (configString) {
                for (const itemId of configString.split(";")) {
                    configItems.add(itemId);
                }
            }
        }

        return configItems.has(offItem);
    };

    return (
        <MenusContainer>
            {items
                ?.filter(
                    item =>
                        (item.type === ItemType.Menu && !isEmpty(item.activeMenuItems)) ||
                        (item.type === ItemType.Submenu && !isHidden(item.id))
                )
                .map(item => {
                    const style: any = {};
                    if (get(theme, "sideMenu.menu.border")) {
                        if (useSubMenuHighlight && item.type === ItemType.Submenu && getActiveChild(item)) {
                            const borderColor = get(theme, "sideMenu.menu.activeBorderColor", "#223861");
                            style.border = `2px solid ${borderColor}`;
                        } else {
                            const borderColor = get(theme, "sideMenu.menu.borderColor", "#cacaca");
                            style.border = `1px solid ${borderColor}`;
                        }
                    }
                    return (
                        <MenuContainer key={item.id}>
                            {item.type === ItemType.Menu && item.name ? <MenuTitle>{item.name}</MenuTitle> : null}
                            <MenuElement
                                style={style}
                                onClick={() => onMenuClick(item)}
                                onKeyDown={() => onMenuClick(item)}
                            >
                                {renderIcon(item)}
                                <ElementText>
                                    <TextContent>{getElementText(item)}</TextContent>
                                </ElementText>
                                <ChevronContainer>
                                    <ChevronRight size={28} color={get(theme, "sideMenu.chevron.color") || "#000000"} />
                                </ChevronContainer>
                            </MenuElement>
                        </MenuContainer>
                    );
                })}
        </MenusContainer>
    );
};
